import * as React from "react";

import { settingsFieldErrors, type SettingsPatch, type SettingValue } from "api/sapSettings";
import { Badge, Button, FormField, Input, notify, Skeleton } from "Components/ui";
import type { FieldGroup, FieldKind, FieldSpec } from "./fields";

type Values = Record<string, SettingValue<unknown>>;

const toDraft = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value === null || value === undefined ? "" : String(value);
};

const fromDraft = (draft: string, kind: FieldKind): string | string[] | number | null => {
  const trimmed = draft.trim();
  if (kind === "list") {
    return trimmed
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (kind === "number") {
    return trimmed === "" ? null : Number(trimmed);
  }
  return trimmed;
};

/** Where a value comes from, in words. */
const sourceHint = (setting: SettingValue<unknown> | undefined): string => {
  if (!setting) {
    return "";
  }
  if (setting.source === "saved") {
    return "Saved in SAP";
  }
  if (setting.source === "env") {
    return `From the server (${setting.env})`;
  }
  return setting.source === "default" ? "Default" : "Not set";
};

interface SettingsFormProps {
  title: string;
  description: string;
  groups: FieldGroup[];
  values: Values | undefined;
  loading: boolean;
  canEdit: boolean;
  onSave: (patch: SettingsPatch) => Promise<unknown>;
  saving: boolean;
}

/**
 * One section of the SAP settings drawer. Fields show the value in effect and where it comes from; saving
 * sends only what changed, and "Use server value" forgets a saved value.
 */
export const SettingsForm: React.FC<SettingsFormProps> = ({
  title,
  description,
  groups,
  values,
  loading,
  canEdit,
  onSave,
  saving,
}) => {
  const fields = React.useMemo<FieldSpec[]>(
    () => groups.reduce<FieldSpec[]>((acc, group) => acc.concat(group.fields), []),
    [groups]
  );
  const initial = React.useMemo<Record<string, string>>(
    () =>
      fields.reduce<Record<string, string>>((acc, field) => {
        acc[field.key] = toDraft(values ? values[field.key]?.value : null);
        return acc;
      }, {}),
    [fields, values]
  );
  const [drafts, setDrafts] = React.useState<Record<string, string>>(initial);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  React.useEffect(() => {
    setDrafts(initial);
    setErrors({});
  }, [initial]);

  const changed = fields.filter((field) => drafts[field.key] !== initial[field.key]);

  const save = async (patch: SettingsPatch, message: string) => {
    try {
      await onSave(patch);
      notify.success(message);
    } catch (err) {
      const fieldErrors = settingsFieldErrors(err);
      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length === 0) {
        notify.error(err, { fallback: "Couldn't save the settings" });
      }
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const patch = changed.reduce<SettingsPatch>((acc, field) => {
      acc[field.key] = fromDraft(drafts[field.key], field.kind);
      return acc;
    }, {});
    save(patch, `${title} saved`);
  };

  return (
    <section aria-label={title} className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        {!canEdit && <Badge className="shrink-0">View only</Badge>}
      </div>
      <form onSubmit={submit} className="space-y-6" noValidate>
        {loading || !values ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-16" />
            ))}
          </div>
        ) : (
          groups.map((group) => (
            <fieldset key={group.title} className="space-y-3">
              <legend className="text-sm font-medium text-foreground">{group.title}</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                {group.fields.map((field) => {
                  const setting = values[field.key];
                  const forget =
                    canEdit && setting?.source === "saved" ? (
                      <Button
                        type="button"
                        variant="link"
                        size="xs"
                        disabled={saving}
                        onClick={() => save({ [field.key]: null }, `${field.label}: server value`)}
                      >
                        Use server value
                      </Button>
                    ) : undefined;
                  return (
                    <FormField
                      key={field.key}
                      label={field.unit ? `${field.label} (${field.unit})` : field.label}
                      labelAction={forget}
                      error={errors[field.key]}
                      hint={[sourceHint(setting), field.hint].filter(Boolean).join(" · ")}
                    >
                      <Input
                        type={
                          field.kind === "number"
                            ? "number"
                            : field.kind === "email"
                            ? "email"
                            : "text"
                        }
                        inputMode={field.kind === "number" ? "numeric" : undefined}
                        value={drafts[field.key] ?? ""}
                        placeholder={field.placeholder}
                        disabled={!canEdit || saving}
                        className={
                          field.kind === "url" ||
                          field.key === "gstin" ||
                          field.key.endsWith("Template")
                            ? "font-mono"
                            : undefined
                        }
                        onChange={(event) =>
                          setDrafts((current) => ({ ...current, [field.key]: event.target.value }))
                        }
                      />
                    </FormField>
                  );
                })}
              </div>
            </fieldset>
          ))
        )}
        {canEdit && (
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-border pt-4">
            {changed.length > 0 && (
              <>
                <span className="text-xs text-muted-foreground">
                  {changed.length === 1 ? "1 change" : `${changed.length} changes`}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={() => setDrafts(initial)}>
                  Discard
                </Button>
              </>
            )}
            <Button type="submit" size="sm" loading={saving} disabled={changed.length === 0}>
              Save
            </Button>
          </div>
        )}
      </form>
    </section>
  );
};

export default SettingsForm;
